
import { Component, ChangeDetectionStrategy, inject, signal, OnInit, computed } from '@angular/core';
import { CommonModule, DecimalPipe, DatePipe } from '@angular/common';
import { Plan, Subscription, UsageStats, StoreWallet, WalletTransaction } from '../../models/index';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { AuthService } from '../../services/auth.service';
import { BillingService } from '../../services/billing.service';
import { PaymentGatewayModalComponent } from '../payment-gateway-modal/payment-gateway-modal.component';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'app-billing-page',
  standalone: true,
  imports: [CommonModule, TranslatePipe, PaymentGatewayModalComponent, DecimalPipe, DatePipe, FormsModule],
  templateUrl: './billing-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingPageComponent implements OnInit {
  private billingService = inject(BillingService);
  private authService = inject(AuthService); 
  private notificationService = inject(NotificationService);

  readonly Infinity = Infinity;

  plans = signal<Plan[]>([]);
  subscription = signal<Subscription | null>(null);
  usage = signal<UsageStats | null>(null);
  
  walletBalance = signal<StoreWallet>({ balance: 0, updated_at: '' });
  walletHistory = signal<WalletTransaction[]>([]);
  
  isLoading = signal(true);
  error = signal<string | null>(null);

  isPaymentModalOpen = signal(false);
  selectedPlan = signal<Plan | null>(null);
  paymentMode = signal<'SUBSCRIPTION' | 'TOPUP'>('SUBSCRIPTION');
  topUpAmount = signal<number>(0);

  billingCycle = signal<'month' | 'year'>('month');

  // Business Type & Plan Filtering
  businessType = computed(() => this.authService.currentStore()?.businessType || 'CLINIC');
  
  // FIXED: More robust filtering for Clinic Plans
  private clinicPlans = computed(() => 
    this.plans()
      .filter(p => !p.id.toLowerCase().startsWith('tier_d')) // Exclude Dispensary ids (d, ds, de)
      .filter(p => (p.interval || 'month') === this.billingCycle())
  );
  
  // FIXED: More robust filtering for Dispensary Plans
  private dispensaryPlans = computed(() => 
    this.plans()
      .filter(p => p.id.toLowerCase().startsWith('tier_d')) // Include only Dispensary ids
      .filter(p => (p.interval || 'month') === this.billingCycle())
  );
    
  displayPlans = computed(() => {
    const plans = this.businessType() === 'DISPENSARY' ? this.dispensaryPlans() : this.clinicPlans();
    console.log(`[Billing] Displaying ${plans.length} plans for ${this.businessType()} (${this.billingCycle()})`);
    return plans;
  });

  billingSummary = computed(() => {
    const sub = this.subscription();
    const stats = this.usage();
    
    if (!sub || !sub.plan || !stats) return null;

    const limit = sub.plan.limits['medical_cases'] || Infinity;
    const used = stats.medical_cases_used;
    const overageCount = Math.max(0, used - limit);
    const overageRate = sub.plan.limits['overage_cost'] || 0;
    
    // Overage Fee calculated for display purposes (Value consumed)
    const overageFee = overageCount * overageRate;
    
    const basePrice = sub.plan.price_monthly; 
    
    // UPDATED: Pre-paid Model
    // Overages are deducted from Wallet instantly, so "Pending to pay" is 0.
    // Logic: In a pre-paid system, you don't owe money at the end of the month for usage,
    // because you already paid for it transaction-by-transaction.
    const totalEstimated = 0; 

    const progressPercent = limit === Infinity ? 0 : Math.min(100, (used / limit) * 100);

    return {
      limit, used, overageCount, overageRate, overageFee, basePrice, totalEstimated, progressPercent,
      interval: sub.plan.interval || 'month'
    };
  });

  ngOnInit(): void {
    this.loadBillingData();
  }

  async loadBillingData(): Promise<void> {
    this.isLoading.set(true);
    this.error.set(null);
    try {
      const [plans, subscription, usage, wallet, history] = await Promise.all([
        this.billingService.getPlans(),
        this.billingService.getSubscription(),
        this.billingService.getUsageStats(),
        this.billingService.getWalletBalance(),
        this.billingService.getWalletTransactions()
      ]);
      
      console.log(`[Billing] Fetched ${plans.length} raw plans from DB`);
      
      // Ensure prices are valid numbers and sort
      this.plans.set(plans.sort((a, b) => Number(a.price_monthly) - Number(b.price_monthly)));
      this.subscription.set(subscription);
      this.usage.set(usage);
      this.walletBalance.set(wallet);
      this.walletHistory.set(history);

      if (subscription?.plan?.interval === 'year') {
          this.billingCycle.set('year');
      }
    } catch (e) {
      console.error("Billing load error:", e);
      this.error.set(e instanceof Error ? e.message : 'Failed to load billing information.');
    } finally {
      this.isLoading.set(false);
    }
  }
  
  setBillingCycle(cycle: 'month' | 'year'): void {
      this.billingCycle.set(cycle);
  }

  initiateUpgrade(plan: Plan): void {
    this.paymentMode.set('SUBSCRIPTION');
    this.selectedPlan.set(plan);
    this.isPaymentModalOpen.set(true);
  }

  initiateTopUp(amount: number): void {
      this.paymentMode.set('TOPUP');
      this.topUpAmount.set(amount);
      this.selectedPlan.set({
          id: 'topup',
          name: `เติมเครดิต (Top-up) ${amount} Credits`,
          price_monthly: amount,
          features: {},
          limits: { users: 0, medical_cases: 0 }
      });
      this.isPaymentModalOpen.set(true);
  }

  async onPaymentComplete(): Promise<void> {
    this.isLoading.set(true);
    try {
        await this.authService.reloadCurrentUserProfile();
        await this.authService.reloadPermissions();
        await this.loadBillingData();
        this.isPaymentModalOpen.set(false);
    } catch(e) {
        this.notificationService.showError('Data refresh failed.');
        this.isLoading.set(false);
    }
  }
}
